<template>
  <div class="metro-gate-chart-container">
    <div 
      ref="container" 
      class="metro-gate-chart"
      :class="{ 'loading': !isInViewport || !hasRendered }"
    >
      <div v-if="!isInViewport || !hasRendered" class="chart-placeholder">
        <div class="placeholder-content">
          <div class="placeholder-icon">📊</div>
          <div class="placeholder-text">图表加载中...</div>
        </div>
      </div>
    </div>
    <!-- 数据来源注释 -->
    <div class="data-source">
      数据来源：上海地铁官方网站
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as d3 from 'd3';
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';

// 数据接口定义
interface StationData {
  line: string
  station: string
  gateCount: number
  stations: number
  totalGates: number
}

interface ProcessedData extends StationData {
  lineColor: string
  averageGates: number
}

const container = ref<HTMLDivElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;
let resizeTimer: number | null = null;
const isInViewport = ref(false);
const hasRendered = ref(false);

// 地铁线路颜色配置
const lineColors: Record<string, string> = {
  '1号线': '#E3002B',
  '2号线': '#82BF25', 
  '3号线': '#FCD600',
  '4号线': '#461D84',
  '5号线': '#944D9A',
  '6号线': '#D40068',
  '7号线': '#ED6F00',
  '8号线': '#0094D8',
  '9号线': '#87CAED',
  '10号线': '#C6AFD4',
  '11号线': '#871C2B',
  '12号线': '#007A60',
  '13号线': '#E999C0',
  '14号线': '#626020',
  '15号线': '#BCA886',
  '16号线': '#98D1C0',
  '17号线': '#BC796F',
  '18号线': '#C4984F'
}

// 响应式数据
const gateData = ref<StationData[]>([])

// 解析CSV数据
const parseCSVData = async () => {
  try {
    const response = await fetch('/gate.csv')
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const csvText = await response.text()
    
    // 更精确的CSV解析 - 处理多行和引号
    const lines = csvText.split('\n')
    const processedData: StationData[] = []
    const lineDataMap = new Map<string, { totalGates: number; stations: number }>()
    
    let currentLine = ''
    let isMultiLineField = false
    let buffer = ''
    
    for (let i = 1; i < lines.length; i++) { // 跳过标题行
      let line = lines[i].trim()
      if (!line) continue
      
      // 处理多行字段（被引号包围的字段）
      if (isMultiLineField) {
        buffer += '\n' + line
        if (line.includes('"')) {
          isMultiLineField = false
          line = buffer
          buffer = ''
        } else {
          continue
        }
      } else if (line.includes('"') && !line.endsWith('"')) {
        isMultiLineField = true
        buffer = line
        continue
      }
      
      // 简单分割CSV字段
      const fields = line.split(',')
      if (fields.length < 3) continue
      
      const lineName = fields[0].trim()
      const totalGatesStr = fields[1].trim()
      const stationsStr = fields[2].trim()
      
      // 只处理有线路名称的行（主要数据行）
      if (lineName && lineName.includes('号线') && totalGatesStr && stationsStr) {
        const totalGates = parseInt(totalGatesStr) || 0
        const stations = parseInt(stationsStr) || 0
        
        if (totalGates > 0 && stations > 0) {
          // 如果这条线路还没有记录，或者当前数据更完整，则更新
          if (!lineDataMap.has(lineName)) {
            lineDataMap.set(lineName, { totalGates, stations })
          }
          currentLine = lineName
        }
      }
    }
    
    // 转换为最终数据格式
    lineDataMap.forEach((data, lineName) => {
      processedData.push({
        line: lineName,
        station: lineName,
        gateCount: Math.round(data.totalGates / data.stations),
        stations: data.stations,
        totalGates: data.totalGates
      })
    })
    
    if (processedData.length > 0) {
      gateData.value = processedData.sort((a, b) => {
        const getLineNumber = (name: string) => {
          const match = name.match(/(\d+)号线/)
          return match ? parseInt(match[1]) : 999
        }
        return getLineNumber(a.line) - getLineNumber(b.line)
      })
    } else {
      throw new Error('没有解析到有效数据')
    }
    
  } catch (error) {
    // 提供模拟数据
    gateData.value = [
      { line: '1号线', station: '1号线', gateCount: 6, stations: 28, totalGates: 178 },
      { line: '2号线', station: '2号线', gateCount: 6, stations: 30, totalGates: 177 },
      { line: '3号线', station: '3号线', gateCount: 4, stations: 29, totalGates: 124 },
      { line: '4号线', station: '4号线', gateCount: 5, stations: 26, totalGates: 129 },
      { line: '5号线', station: '5号线', gateCount: 3, stations: 19, totalGates: 49 },
      { line: '6号线', station: '6号线', gateCount: 3, stations: 28, totalGates: 88 },
      { line: '7号线', station: '7号线', gateCount: 5, stations: 33, totalGates: 155 },
      { line: '8号线', station: '8号线', gateCount: 5, stations: 30, totalGates: 136 },
      { line: '9号线', station: '9号线', gateCount: 5, stations: 35, totalGates: 181 },
      { line: '10号线', station: '10号线', gateCount: 4, stations: 32, totalGates: 145 },
      { line: '11号线', station: '11号线', gateCount: 4, stations: 38, totalGates: 152 },
      { line: '12号线', station: '12号线', gateCount: 3, stations: 32, totalGates: 97 },
      { line: '13号线', station: '13号线', gateCount: 3, stations: 31, totalGates: 89 },
      { line: '16号线', station: '16号线', gateCount: 2, stations: 13, totalGates: 26 },
      { line: '17号线', station: '17号线', gateCount: 2, stations: 13, totalGates: 29 }
    ]
  }
}

function render() {
  if (!container.value || !gateData.value.length || !isInViewport.value) return;
  
  // 如果已经渲染过，则不重复渲染动画
  if (hasRendered.value) return;
  hasRendered.value = true;
  
  const element = container.value;
  const width = element.clientWidth;
  const height = 500;
  const margin = { top: 60, right: 40, bottom: 100, left: 90 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  element.innerHTML = '';
  
  const svg = d3.select(element)
    .append('svg')
    .attr('width', width)
    .attr('height', height);
  
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);
  
  // 数据处理
  const processedData: ProcessedData[] = gateData.value.map(d => ({
    ...d,
    lineColor: lineColors[d.line] || '#6b7280',
    averageGates: d.totalGates / d.stations
  }));
  
  // 比例尺
  const xScale = d3.scaleBand()
    .domain(processedData.map(d => d.line))
    .range([0, innerWidth])
    .padding(0.3);
  
  const yScale = d3.scaleLinear()
    .domain([0, d3.max(processedData, d => d.totalGates) || 200])
    .range([innerHeight, 0])
    .nice();
  
  const radiusScale = d3.scaleLinear()
    .domain([0, d3.max(processedData, d => d.stations) || 40])
    .range([8, 25]);
  
  // 背景网格线
  const yTicks = yScale.ticks(6);
  yTicks.forEach(tick => {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(tick))
      .attr('y2', yScale(tick))
      .attr('stroke', '#f1f5f9')
      .attr('stroke-width', 1)
      .attr('opacity', 0.8);
  });
  
  // 绘制气泡
  const bubbles = g.selectAll('.bubble')
    .data(processedData)
    .enter()
    .append('g')
    .attr('class', 'bubble')
    .attr('transform', d => `translate(${(xScale(d.line) || 0) + xScale.bandwidth() / 2}, ${yScale(d.totalGates)})`);
  
  // 主气泡（总出入口数）- 使用地铁线路颜色
  bubbles.append('circle')
    .attr('r', 0)
    .attr('fill', d => d.lineColor)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 3)
    .attr('opacity', 0.85)
    .style('cursor', 'pointer')
    .style('filter', 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))')
    .on('mouseenter', function(event, d) {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('stroke-width', 4)
        .attr('opacity', 1)
        .attr('transform', 'scale(1.15)')
        .style('filter', 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25))');
      
      showTooltip(d, this);
    })
    .on('mouseleave', function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('stroke-width', 3)
        .attr('opacity', 0.85)
        .attr('transform', 'scale(1)')
        .style('filter', 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))');
      
      hideTooltip();
    })
    .transition()
    .duration(1000)
    .delay((d, i) => i * 100)
    .attr('r', d => radiusScale(d.stations));
  
  // 内部小圆圈（平均每站出入口数）- 使用相同颜色的更深色调
  bubbles.append('circle')
    .attr('r', 0)
    .attr('fill', d => d3.color(d.lineColor)?.darker(0.5)?.toString() || '#ffffff')
    .attr('opacity', 0.9)
    .style('pointer-events', 'none')
    .transition()
    .duration(800)
    .delay((d, i) => i * 100 + 200)
    .attr('r', d => radiusScale(d.stations) * 0.5);

  // 出入口总数标签
  bubbles.append('text')
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '10px')
    .attr('font-weight', '700')
    .attr('fill', '#ffffff')
    .style('opacity', 0)
    .style('text-shadow', '1px 1px 2px rgba(0, 0, 0, 0.5)')
    .style('pointer-events', 'none')
    .text(d => `${d.totalGates}`)
    .transition()
    .duration(500)
    .delay((d, i) => i * 100 + 1200)
    .style('opacity', 1);
  
  // Y轴
  const yAxis = d3.axisLeft(yScale)
    .tickFormat(d => `${d}`)
    .tickSize(-innerWidth)
    .tickPadding(15);
  
  g.append('g')
    .attr('class', 'y-axis')
    .call(yAxis)
    .selectAll('text')
    .attr('font-size', '12px')
    .attr('fill', '#6b7280');
  
  g.select('.y-axis').selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.3);
  
  g.select('.y-axis').select('.domain').remove();
  
  // X轴
  const xAxis = d3.axisBottom(xScale)
    .tickFormat(d => d)
    .tickSizeOuter(0);
  
  const xAxisGroup = g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0, ${innerHeight})`)
    .call(xAxis);
    
  // X轴标签样式 - 使用对应线路颜色
  xAxisGroup.selectAll('text')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', d => {
      const lineData = processedData.find(item => item.line === d);
      return lineData ? lineData.lineColor : '#374151';
    })
    .attr('transform', 'rotate(-45)')
    .style('text-anchor', 'end')
    .attr('dx', '-0.5em')
    .attr('dy', '0.15em');
  
  // X轴线条样式
  xAxisGroup.select('.domain')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-width', 1);
    
  xAxisGroup.selectAll('.tick line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.5);
  
  // Y轴标题
  g.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', -70)
    .attr('x', -innerHeight / 2)
    .attr('text-anchor', 'middle')
    .attr('font-size', '14px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text('出入口总数 (个)');
  
  // 图例 - 居中靠上
  const legend = g.append('g')
    .attr('transform', `translate(${innerWidth / 2}, -30)`);
  
  // 主气泡图例
  const legendMain = legend.append('g')
    .attr('transform', 'translate(-80, 0)');
  
  legendMain.append('circle')
    .attr('r', 12)
    .attr('fill', '#3b82f6')
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 3)
    .attr('opacity', 0.85);
  
  legendMain.append('text')
    .attr('x', -20)
    .attr('y', 0)
    .attr('dominant-baseline', 'middle')
    .attr('text-anchor', 'end')
    .attr('font-size', '12px')
    .attr('fill', '#374151')
    .text('出入口总数');
  
  // 内部圆圈图例
  const legendInner = legend.append('g')
    .attr('transform', 'translate(80, 0)');
  
  legendInner.append('circle')
    .attr('r', 8)
    .attr('fill', '#1e40af')
    .attr('opacity', 0.9);
  
  legendInner.append('text')
    .attr('x', -15)
    .attr('y', 0)
    .attr('dominant-baseline', 'middle')
    .attr('text-anchor', 'end')
    .attr('font-size', '12px')
    .attr('fill', '#374151')
    .text('站点规模');
  
  // 提示框函数
  function showTooltip(data: ProcessedData, element: SVGCircleElement) {
    const tooltip = d3.select('body').selectAll('.gate-tooltip').data([0]);
    
    const tooltipEnter = tooltip.enter()
      .append('div')
      .attr('class', 'gate-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px 16px')
      .style('border-radius', '8px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)')
      .style('backdrop-filter', 'blur(10px)')
      .style('opacity', 0);
    
    const tooltipUpdate = tooltipEnter.merge(tooltip as any);
    
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    tooltipUpdate
      .html(`
        <div style="font-weight: bold; color: ${data.lineColor}; margin-bottom: 8px;">
          ${data.line}
        </div>
        <div style="margin-bottom: 4px;">
          <strong>站点数量：</strong>${data.stations}个
        </div>
        <div style="margin-bottom: 4px;">
          <strong>出入口总数：</strong>${data.totalGates}个
        </div>
        <div>
          <strong>平均每站：</strong>${data.averageGates.toFixed(1)}个出入口
        </div>
      `)
      .style('left', (rect.left + scrollLeft + 10) + 'px')
      .style('top', (rect.top + scrollTop - 10) + 'px')
      .transition()
      .duration(200)
      .style('opacity', 1);
  }
  
  function hideTooltip() {
    d3.select('body').selectAll('.gate-tooltip')
      .transition()
      .duration(200)
      .style('opacity', 0)
      .remove();
  }
}

// 生命周期钩子
onMounted(async () => {
  await parseCSVData()
  
  // 设置视口观察器
  if (container.value) {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasRendered.value) {
            isInViewport.value = true;
            render();
          }
        });
      },
      {
        threshold: 0.3, // 当30%的元素进入视口时触发
        rootMargin: '50px' // 提前50px触发
      }
    );
    
    intersectionObserver.observe(container.value);
  }
  
  // 响应式调整
  resizeObserver = new ResizeObserver(() => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(() => {
      if (hasRendered.value) {
        // 如果已经渲染过，允许重新渲染以适应尺寸变化
        const wasRendered = hasRendered.value;
        hasRendered.value = false;
        render();
        hasRendered.value = wasRendered;
      }
    }, 300)
  })
  
  if (container.value) {
    resizeObserver.observe(container.value)
  }
})

// 监听数据变化
watch(gateData, () => {
  if (isInViewport.value) {
    hasRendered.value = false;
    render();
  }
}, { deep: true })

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect()
  }
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
})
</script>

<style scoped>
.metro-gate-chart-container {
  position: relative;
  width: 100%;
  height: 500px;
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

.metro-gate-chart {
  width: 100%;
  height: 500px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  /* 确保字体使用思源黑体 */
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
}

.metro-gate-chart.loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.placeholder-content {
  text-align: center;
  color: #6b7280;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.7;
  animation: pulse 2s infinite;
}

.placeholder-text {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.8;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.chart-container {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stats-container {
  margin-bottom: 30px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #dc2626;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.legend-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.legend-container h4 {
  margin: 0 0 16px 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
}

.legend-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.legend-item:hover {
  background-color: #f9fafb;
}

.legend-item.active {
  border-color: var(--line-color);
  background-color: rgba(var(--line-color-rgb, 220, 38, 38), 0.05);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--line-color);
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.legend-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.legend-line {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.legend-stats {
  font-size: 0.75rem;
  color: #6b7280;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .metro-gate-chart-container {
    padding: 16px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .stat-item {
    padding: 16px;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .legend-grid {
    grid-template-columns: 1fr;
  }
  
  .legend-item {
    padding: 10px;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
}
</style>